/**
 * The time range of a table with times (the call log, an issue's calls, the
 * Logboek), as `<TimeRangeFilter>` picks it: one of the periods (`?days=30`,
 * the past week, month or quarter) or a custom range from one moment to
 * another, picked as a day and a time in Belgian time and kept in the URL as
 * ISO moments with their offset (`?from=2026-10-12T08:00:00.000+02:00&to=…`).
 * A custom range takes precedence over `days`; writing one drops the other.
 *
 *     const [range, setRange] = useTimeRange();
 *     const calls = useAdminMcpCalls({ ...timeRangeQuery(range), … });
 *     <TimeRangeFilter value={range} onChange={setRange} />
 */

import { useLocation, useSearchParams } from "react-router";

import { isDay } from "@/lib/calendar";
import { belgianDay, formatTime, TIME_ZONE } from "@/lib/format";
import { parsePeriod, type Period, PERIODS } from "@/lib/period";

/** A table's time range: a period of days up to now, or a custom range. */
export type TimeRange =
  | { kind: "period"; days: Period }
  | {
      kind: "custom";
      /** The first moment, ISO with its offset: "2026-10-12T08:00:00.000+02:00". */
      from: string;
      /** The last moment, the whole of its minute included: "2026-10-14T17:00:59.999+02:00". */
      to: string;
    };

/** The longest custom range, in days, as the API accepts it. */
export const MAX_RANGE_DAYS = 366;

/**
 * The longest custom range in milliseconds: the API refuses a `to` more than
 * `MAX_RANGE_DAYS` days of 24 hours after its `from`, so the end's day counts
 * only up to the start's time.
 */
const MAX_RANGE_MS = MAX_RANGE_DAYS * 24 * 60 * 60 * 1000;

/** An ISO moment that names its offset ("+02:00", "Z"), as the API needs it. */
const WITH_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/** The range of a page opened without one: the first period, the past week. */
export const DEFAULT_TIME_RANGE: TimeRange = { kind: "period", days: PERIODS[0] };

/** The search parameters a time range lives in. */
const PARAMS = { days: "days", from: "from", to: "to" } as const;

/** A time as typed: "08:00", "8:00", "8.00" or "0800". */
const TYPED_TIME = /^(\d{1,2})[:.h]?(\d{2})$/;

/** A time typed in a box as "HH:MM" ("8.5" or "25:00" are none); null when it is not one. */
export function parseTime(value: string): string | null {
  const match = TYPED_TIME.exec(value.trim());
  if (!match) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) {
    return null;
  }
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

/** Brussels' offset from UTC at a moment, in minutes: 120 in summer, 60 in winter. */
function brusselsOffset(at: number): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(at));
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const local = Date.UTC(
    part("year"),
    part("month") - 1,
    part("day"),
    part("hour"),
    part("minute"),
    part("second"),
  );
  return Math.round((local - Math.floor(at / 1000) * 1000) / 60_000);
}

/** An offset in minutes as ISO writes it: "+02:00". */
function offsetText(minutes: number): string {
  const sign = minutes < 0 ? "-" : "+";
  const abs = Math.abs(minutes);
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}

/**
 * A day and a time in Belgian time as an ISO moment with Brussels' offset of
 * that moment: "2026-10-12" and "08:00" give "2026-10-12T08:00:00.000+02:00".
 * `endOfMinute` takes the minute's last millisecond, so a range's end
 * includes its whole minute. Null for no day or no time.
 */
export function belgianMoment(day: string, time: string, endOfMinute = false): string | null {
  const hhmm = parseTime(time);
  if (!isDay(day) || hhmm === null) {
    return null;
  }
  const [year, month, date] = day.split("-").map(Number) as [number, number, number];
  const [hour, minute] = hhmm.split(":").map(Number) as [number, number];
  const wall = Date.UTC(year, month - 1, date, hour, minute);
  // The offset at the wall time read as UTC, then again at the moment it gives, for the
  // days the clocks change.
  const first = brusselsOffset(wall - brusselsOffset(wall) * 60_000);
  const offset = brusselsOffset(wall - first * 60_000);
  const seconds = endOfMinute ? "59.999" : "00.000";
  return `${day}T${hhmm}:${seconds}${offsetText(offset)}`;
}

/** A moment's day and time in Belgian time, for the boxes: "2026-10-12" and "08:00". */
export function momentParts(moment: string): { day: string; time: string } | null {
  const day = belgianDay(moment);
  return day ? { day, time: formatTime(moment) } : null;
}

/** Why a custom range cannot be used, or null when it can. */
export type RangeProblem = "day" | "time" | "order" | "tooLong";

/** What the API refuses in a range: an end not after its start, or over `MAX_RANGE_MS` later. */
function rangeProblem(start: string, end: string): "order" | "tooLong" | null {
  const span = Date.parse(end) - Date.parse(start);
  if (span <= 0) {
    return "order";
  }
  return span > MAX_RANGE_MS ? "tooLong" : null;
}

/**
 * A custom range from the boxes, or why not: a day and a time on both ends,
 * the end after the start, and at most `MAX_RANGE_DAYS` days of 24 hours
 * apart, the API's own rule.
 */
export function customRange(
  from: { day: string | null; time: string },
  to: { day: string | null; time: string },
): { range: Extract<TimeRange, { kind: "custom" }> } | { problem: RangeProblem } {
  if (!from.day || !to.day) {
    return { problem: "day" };
  }
  const start = belgianMoment(from.day, from.time);
  const end = belgianMoment(to.day, to.time, true);
  if (start === null || end === null) {
    return { problem: "time" };
  }
  const problem = rangeProblem(start, end);
  return problem ? { problem } : { range: { kind: "custom", from: start, to: end } };
}

/**
 * An ISO moment from a URL: a "+" in a hand-typed link arrives as a space.
 * Null for a moment without its offset, which the browser would read in its
 * own time zone and the API refuses.
 */
function urlMoment(value: string | null): string | null {
  if (!value) {
    return null;
  }
  const moment = value.replace(" ", "+");
  return WITH_OFFSET.test(moment) && !Number.isNaN(Date.parse(moment)) ? moment : null;
}

/**
 * How a page keeps its time range in the URL:
 * - `defaultDays`: the period it opens on, left out of the URL (the first
 *   period, the past week, unless the page opens on another);
 * - `keepPeriod`: a custom range leaves `?days=` as it is, for a page whose
 *   other figures follow that period while a table shows the custom range
 *   (an issue's page, the Logboek). Otherwise a custom range drops it.
 */
export interface TimeRangeUrl {
  defaultDays?: Period;
  keepPeriod?: boolean;
}

/**
 * The time range in a page's search parameters: a custom range the API takes
 * first (both moments with their offset, the end after the start, at most
 * `MAX_RANGE_DAYS` days apart), else the period, or the page's default.
 */
export function parseTimeRange(
  params: URLSearchParams,
  { defaultDays = PERIODS[0] }: TimeRangeUrl = {},
): TimeRange {
  const from = urlMoment(params.get(PARAMS.from));
  const to = urlMoment(params.get(PARAMS.to));
  if (from && to && rangeProblem(from, to) === null) {
    return { kind: "custom", from, to };
  }
  return { kind: "period", days: parsePeriod(params.get(PARAMS.days), defaultDays) };
}

/**
 * The page's search parameters with a time range: the period (left out at the
 * page's default) or the custom range, each dropping the other, unless the
 * page keeps its period beside a custom range (`keepPeriod`). Every other
 * parameter stays.
 */
export function withTimeRangeParams(
  params: URLSearchParams,
  range: TimeRange,
  { defaultDays = PERIODS[0], keepPeriod = false }: TimeRangeUrl = {},
): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(PARAMS.from);
  next.delete(PARAMS.to);
  if (range.kind === "custom") {
    if (!keepPeriod) {
      next.delete(PARAMS.days);
    }
    next.set(PARAMS.from, range.from);
    next.set(PARAMS.to, range.to);
  } else if (range.days === defaultDays) {
    next.delete(PARAMS.days);
  } else {
    next.set(PARAMS.days, String(range.days));
  }
  return next;
}

/** What the API takes for a time range: `days`, or `from` and `to`. */
export function timeRangeQuery(range: TimeRange): { days?: number; from?: string; to?: string } {
  return range.kind === "custom" ? { from: range.from, to: range.to } : { days: range.days };
}

/** Whether two ranges are the same, such as a table's range and its page's default. */
export function sameTimeRange(a: TimeRange, b: TimeRange): boolean {
  if (a.kind === "period" && b.kind === "period") {
    return a.days === b.days;
  }
  return a.kind === "custom" && b.kind === "custom" && a.from === b.from && a.to === b.to;
}

/**
 * A page's time range kept in its URL (`?days=` or `?from=&to=`, as `url`
 * says), replacing the history entry as the filters do and leaving every
 * other parameter and the entry's state (an issue page's way back to its
 * list).
 */
export function useTimeRange(url: TimeRangeUrl = {}): [TimeRange, (range: TimeRange) => void] {
  const [params, setParams] = useSearchParams();
  const { state } = useLocation();
  return [
    parseTimeRange(params, url),
    (range) =>
      setParams((current) => withTimeRangeParams(current, range, url), {
        replace: true,
        state,
      }),
  ];
}
